import { Card, CardProps, Flex, Typography } from "@xcorejs/ui";
import { useEffect, useRef, useState } from "react";
import { useLayout } from "xcore";
import { ProductOverviewPage } from "xcore/types";
import styled from "styled-components";
import { Skeleton } from "@mui/joy";
import { resolveSrcSet } from "utils";

export type ProductOverviewCardValue = Pick<ProductOverviewPage<"thumbnailTitle" | "title" | "thumbImage">, "values">;

type ProductOverviewCardProps = {
  value: ProductOverviewCardValue;
} & CardProps;

const ProductOverviewCard = ({ value, ...props }: ProductOverviewCardProps) => {
  const { image, localize } = useLayout();
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const [showLoading, setShowLoading] = useState<boolean>(false); // Shows debounced loading if image is still being downloaded
  const timeUntilShowLoading = 400; // in ms

  const imgRef = useRef<HTMLImageElement | null>(null);

  const text = localize(value.values.thumbnailTitle) || localize(value.values.title);
  const thumbnail = value.values.thumbImage ? image(value.values.thumbImage, { width: 300, height: 300 }) : null;
  const retinaThumbnail = value.values.thumbImage ? image(value.values.thumbImage, { width: 300, height: 300, enlargement: 2 }) : null;

  // Check on mount if the image is already loaded from cache
  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsImageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isImageLoading) {
      // Show loading with debounce
      const timeout = setTimeout(() => setShowLoading(true), timeUntilShowLoading);

      return () => clearTimeout(timeout);
    }

    setShowLoading(false); // Do not show loading if image is loaded
  }, [isImageLoading]);

  return (
    <Card
      variant="outline"
      body={
        <CardContainer>
          {thumbnail && (
            <>
              {showLoading && <Skeleton variant="rectangular" width="100%" height="100%" />}{" "}
              {/* Skeleton loading must be separately. If image would be the child of Skeleton, it would fetch the image twice */}
              <ImageWrapper>
                <img
                  ref={imgRef}
                  src={thumbnail}
                  {...resolveSrcSet(thumbnail, retinaThumbnail)}
                  alt={text ?? ""}
                  style={{ maxWidth: "100%" }}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  onLoad={() => setIsImageLoading(false)}
                />
              </ImageWrapper>
            </>
          )}

          {text && (
            <Flex
              position="absolute"
              width="100%"
              height="100%"
              padding="2rem"
              justifyContent="center"
              alignItems="center"
              top={0}
              left={0}
              zIndex={100}
            >
              <Typography
                variant="h4"
                maxWidth="15rem"
                textAlign="center"
                fontSize={{ sm: "2.4rem" }}
                fontWeight={600}
                lineHeight={{ sm: "3.6rem" }}
                color="#fff"
              >
                {text}
              </Typography>
            </Flex>
          )}

          <Overlay />
        </CardContainer>
      }
      _body={{ position: "relative", padding: 0, height: "100%", bg: "darkGray" }}
      border="0"
      {...props}
    />
  );
};

export default ProductOverviewCard;

const CardContainer = styled.div`
  position: relative;
  --scale: 1; /* CSS variable for scale */
  transition: transform 500ms;

  /* When hovering the card, update the scale variable */
  &:hover {
    --scale: 1.2;
  }
`;

// The image wrapper uses the scale variable to scale the image
const ImageWrapper = styled.div`
  transform: scale(var(--scale));
  transition: transform 500ms;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #5e6062;
  opacity: 0.75;
  transition: opacity 300ms;
  will-change: opacity;

  /* when the container is hovered, fade out */
  ${CardContainer}:hover & {
    opacity: 0;
  }
`;
